import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRentCreditOptions, redeemRentCredit } from '../../../auth/_requests';
import type { RentCreditOption } from '../../../helpers/utils/types';
import { showToast } from '../../../helpers/utils/showToast';

export const useWalletRentCredits = (token?: string) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<RentCreditOption[]>([]);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRentCreditOptions = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await getRentCreditOptions(token);
        setOptions(res ?? []);
      } catch (error) {
        console.error('Error fetching rent credit options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRentCreditOptions();
  }, [token]);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => Number(a.amount) - Number(b.amount)),
    [options]
  );

  const redeem = useCallback(
    async (option: RentCreditOption, onSuccess?: () => Promise<void> | void) => {
      if (!token) return;
      setRedeemingId(option.rentCreditOptionId);
      try {
        const res = await redeemRentCredit(token, option.rentCreditOptionId);
        if (res.message === 'success') {
          showToast({
            message: `${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(Number(option.amount) || 0)} rent credit added. Points redeemed-credit auto-applies to next payment.`,
            type: 'success',
          });
          if (onSuccess) await onSuccess();
        }
      } catch (error) {
        console.error('Error redeeming rent credit:', error);
        showToast({ message: 'Failed to redeem rent credit. Please try again.', type: 'error' });
      } finally {
        setRedeemingId(null);
      }
    },
    [token]
  );

  return { loading, options: sortedOptions, redeemingId, redeem };
};
